import './PillboxInput.less';
import React from 'react';
import {translate} from '../../i18n';
import Tooltips from '../../scomponents/tooltips/Tooltips';
import Icon from "../icon/Icon";
import {compareArrays} from "../../utils/js";

const TEXT_INPUT_PADDING = 2;

const DEFAULT_SEPARATOR = /[,\n\t]+/;

const DEFAULT_JOINER = ', ';

export default class PillboxInput extends React.Component {
    constructor(props) {
        super(props);

        this.state = {
            pills: PillboxInput.textsToPills(props.defaultValue, props.duplicatable),
            tempText: '',
            tempTextInputWidth: TEXT_INPUT_PADDING,
            focused: false
        };

        /** @type {HTMLInputElement} */
        this.tempTextInput = null;

        /** @type {HTMLLIElement} */
        this.tempTextListItem = null;

        /** @type {CanvasRenderingContext2D} */
        this.context2d = document.createElement('canvas').getContext('2d');

        /** @type {function} */
        this.closeDuplicationTooltip = null;

        /** @type {HTMLUListElement} */
        this.pillListEl = null;
    }

    static getDerivedStateFromProps(props, state) {
        if (props.value !== undefined) {
            state.pills = PillboxInput.textsToPills(props.value, props.duplicatable);

            return state;
        }

        if (props.imperativeValue !== undefined && (state.prevImperativeValue === undefined ||
            !compareArrays(props.imperativeValue, state.prevImperativeValue)))
        {
            state.prevImperativeValue = props.imperativeValue;

            state.pills = PillboxInput.textsToPills(props.imperativeValue, props.duplicatable);

            return state;
        }

        return null;
    }

    /**
     *
     * @param {string[]} texts
     * @param {boolean} duplicatable
     * @return {Pill[]}
     */
    static textsToPills = (texts, duplicatable = false) => {
        if (texts === undefined) {
            return [];
        }
        let cpTexts = texts.map(text => text.trim());
        if (!duplicatable) {
            cpTexts = cpTexts.filter((text, index) => cpTexts.indexOf(text) === index);
        }
        return cpTexts.map(text => new Pill(text));
    };

    /**
     *
     * @param {Pill[]} pills
     * @return {string[]}
     */
    static pillsToTexts = (pills) => {
        return pills.map(pill => pill.text);
    };

    closeDuplicationTooltipIfShowing = () => {
        if (this.closeDuplicationTooltip === null) {
            return;
        }
        this.closeDuplicationTooltip();
        this.closeDuplicationTooltip = null;
    };

    notifyChange = () => {
        const {pills} = this.state;
        const {onChange} = this.props;
        this.forceUpdate(
            onChange !== undefined
                ? () => onChange(PillboxInput.pillsToTexts(pills))
                : undefined
        );
    };

    setFocused = (focused) => {
        this.setState({focused});
    };

    setTempText = (tempText, autoCloseDuplicationTooltip = true) => {
        if (tempText === this.state.tempText) {
            return;
        }
        const style = window.getComputedStyle(this.tempTextInput);
        this.context2d.font = style.font;
        const itemStyle = window.getComputedStyle(this.tempTextListItem);
        const itemMarginX = parseFloat(itemStyle.marginLeft) + parseFloat(itemStyle.marginRight);
        const tempTextInputWidth = Math.min(
            this.pillListEl.clientWidth - itemMarginX,
            this.context2d.measureText(tempText.replace(/\n/g, ' ')).width + TEXT_INPUT_PADDING
        );
        this.setState({
            tempText,
            tempTextInputWidth
        }, () => {
            if (autoCloseDuplicationTooltip) {
                this.closeDuplicationTooltipIfShowing();
            }
        });
    };

    addPillsFromText = (fullText) => {
        const {duplicatable, joiner = DEFAULT_JOINER, separator = DEFAULT_SEPARATOR} = this.props;
        const {pills} = this.state;
        const invalidDuplications = [];
        let hasNewPills = false;
        fullText.split(separator).forEach(text => {
            text = text.trim();
            if (text === '') {
                return;
            }
            if (!duplicatable && pills.some(pill => text === pill.text)) {
                invalidDuplications.push(text);
                return;
            }
            pills.push(new Pill(text));
            hasNewPills = true;
        });
        if (invalidDuplications.length > 0) {
            const tooltipAdded = Tooltips.add(
                <div className="pillbox-pill-errors">
                    {translate('Already existed: ::items', {items: invalidDuplications.join(joiner)})}
                </div>,
                this.tempTextInput,
                {appearance: 'hard'}
            );

            if (tooltipAdded) {
                this.closeDuplicationTooltip = () => Tooltips.remove(this.tempTextInput);
            }
        }
        if (!hasNewPills) {
            return;
        }
        this.setTempText('', false);
        this.notifyChange();
    };

    addTempTextToPills = () => {
        const {tempText} = this.state;
        this.addPillsFromText(tempText);
    };

    removePillByIndex = (index) => {
        const {pills} = this.state;
        if (index < 0 || index > pills.length - 1) {
            return;
        }

        pills.splice(index, 1);
        this.notifyChange();
    };

    clearAllPills = () => {
        const {pills} = this.state;
        if (pills.length === 0) {
            return;
        }
        pills.length = 0;
        this.notifyChange();
    };

    handleKeyEvent = (ev) => {
        const {separator = DEFAULT_SEPARATOR} = this.props;
        const {pills, tempText} = this.state;
        if (
            ev.key === 'Enter' ||
            ev.key === 'Tab' ||
            (ev.key === ',' && separator instanceof RegExp && separator.test(','))
        ) {
            ev.preventDefault();
            this.addTempTextToPills();
            return;
        }

        if (tempText === '' && (ev.key === 'Backspace' || ev.key === 'Delete')) {
            ev.preventDefault();
            this.removePillByIndex(pills.length - 1);
        }
    };

    focusTempTextInput = () => {
        this.tempTextInput.focus();
    };

    handlePasteEvent = ev => {
        ev.preventDefault();

        for (let i = 0; i < ev.clipboardData.items.length; i++) {
            const item = ev.clipboardData.items[i];
            if (item.type === 'text/plain') {
                item.getAsString(str => {
                    this.addPillsFromText(str);
                });
                break;
            }
        }
    };

    render() {
        const {prompt, getTagErrors = tagValue => []} = this.props;
        const {pills, tempText, tempTextInputWidth, focused} = this.state;

        return <div className={'pillbox-input' + (focused ? ' focused' : '')} ref={el => this.componentEl = el}>
            <div
                className="inline"
                onClick={() => {
                    this.focusTempTextInput();
                }}
            >
                <div className="pills">
                    {
                        prompt &&
                        pills.length === 0 &&
                        tempText === '' &&
                        <div className="prompt">
                            <div className="text">{prompt}</div>
                        </div>
                    }
                    <ul ref={el => this.pillListEl = el}>
                        {
                            pills.map((pill, index) => {
                                const errors = getTagErrors(pill.text);
                                return <li key={index}>
                                    <div className="pill">
                                        <div className="content">
                                            {pill.text}
                                        </div>
                                        <div className="buttons">
                                            {
                                                errors.length > 0 &&
                                                <PillErrorButton errors={errors}/>
                                            }
                                            <button
                                                type="button"
                                                className="remove-button"
                                                onClick={() => this.removePillByIndex(index)}
                                            >
                                                <Icon name="times"/>
                                            </button>
                                        </div>
                                    </div>
                                </li>
                            })
                        }
                        <li ref={el => this.tempTextListItem = el}>
                            <input
                                type="text"
                                className="temp-text-input"
                                value={tempText}
                                onKeyUp={ev => this.setTempText(ev.target.value)}
                                onKeyDown={ev => this.handleKeyEvent(ev)}
                                onPaste={ev => this.handlePasteEvent(ev)}
                                onChange={ev => this.setTempText(ev.target.value)}
                                onFocus={() => this.setFocused(true)}
                                onBlur={() => {
                                    this.setFocused(false);
                                    this.addTempTextToPills();
                                }}
                                ref={el => this.tempTextInput = el}
                                style={{
                                    width: tempTextInputWidth + 'px'
                                }}
                            />
                        </li>
                    </ul>
                </div>
                {
                    pills.length > 0 &&
                    <div className="buttons">
                        <button
                            type="button"
                            className="clear-button"
                            onClick={() => this.clearAllPills()}
                        >
                            <Icon name="times"/>
                        </button>
                    </div>
                }
            </div>
        </div>;
    }
}

let autoIncId = 0;

function Pill(text) {
    this.id = ++autoIncId;
    this.text = text;
}

class PillErrorButton extends React.Component {
    getErrorsHtml = () => {
        const {errors} = this.props;

        if (errors.length === 1) {
            return errors[0];
        }

        return `<ul>${errors.map(t => `<li>${t}</li>`).join('')}</ul>`;
    };

    render() {
        return <button
            type="button"
            className="error-button"
            aria-label={this.getErrorsHtml()}
            aria-controls="tooltip-click-to-hold"
        >
            <Icon name="caution-circle"/>
        </button>;
    }
}